import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import axios from "axios";
import {
  FiChevronLeft, FiChevronRight, FiGrid, FiImage, FiList,
  FiRefreshCw, FiSearch, FiUploadCloud, FiX,
} from "react-icons/fi";

import PageHeader from "../../components/ui/PageHeader";
import StatCard from "../../components/ui/StatCard";
import Num from "../../components/ui/Num";

/**
 * Tovar rasmlari.
 *
 * Ilgari bu sahifada bitta "Rasm yuklash" tugmasi turardi va u hech qanday
 * javob ko'rsatmasdi: qaysi rasm ulandi, qaysi biri artikuli topilmagani
 * uchun rad etildi -- bilib bo'lmasdi. Endi har bir fayl bo'yicha natija
 * ko'rinadi.
 */

const API = process.env.REACT_APP_API;

const STATUS = {
  LINKED:         { label: "Ulandi",            color: "success" },
  ALREADY_LINKED: { label: "Allaqachon bor",    color: "default" },
  NO_PRODUCT:     { label: "Artikul topilmadi", color: "warning" },
  NOT_AN_IMAGE:   { label: "Rasm emas",         color: "error" },
  TOO_LARGE:      { label: "Juda katta",        color: "error" },
  FAILED:         { label: "Xato",              color: "error" },
};

/** Fayl nomidan artikul: "14-1-9-0003 (2).jpg" -> "14-1-9-0003" */
const vendorCodeOf = (fileName) => {
  const dot = fileName.lastIndexOf(".");
  const base = dot > 0 ? fileName.slice(0, dot) : fileName;
  const space = base.indexOf(" ");
  return (space > 0 ? base.slice(0, space) : base).trim();
};

const ProductsPage = () => {
  const [overview, setOverview] = useState(null);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [filter, setFilter] = useState("all");
  const inputRef = useRef(null);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/product/photos/overview`, {
        headers: { Authorization: localStorage.getItem("token") },
      });
      setOverview(data);
    } catch {
      setOverview(null);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const upload = async (fileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    setBusy(true);
    setResults([]);
    setProgress({ done: 0, total: files.length });

    const collected = [];
    // Bittalab yuboramiz -- shunda har bir fayl bo'yicha alohida javob keladi
    // va jarayon ko'rinib turadi.
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await axios.post(`${API}/product/update/photo`, form, {
          headers: {
            Authorization: localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          timeout: 180000,
        });
        collected.push(data);
      } catch (err) {
        collected.push({
          fileName: file.name,
          vendorCode: vendorCodeOf(file.name),
          status: err.response?.status === 413 ? "TOO_LARGE" : "FAILED",
          message:
            err.response?.status === 413
              ? "Server faylni qabul qilmadi -- juda katta"
              : err.response?.data?.message || "Yuborib bo'lmadi",
        });
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
      setResults([...collected]);
    }

    setBusy(false);
    loadOverview();
    if (inputRef.current) inputRef.current.value = "";
  };

  const counts = useMemo(() => {
    const c = {};
    results.forEach((r) => {
      c[r.status] = (c[r.status] || 0) + 1;
    });
    return c;
  }, [results]);

  const shown = useMemo(
    () => (filter === "all" ? results : results.filter((r) => r.status === filter)),
    [results, filter]
  );

  const linked = (counts.LINKED || 0) + (counts.ALREADY_LINKED || 0);

  return (
    <Box>
      <PageHeader
        title="Tovar rasmlari"
        subtitle="Rasm fayl nomidagi artikul bo'yicha tovarga ulanadi."
        actions={
          <Button
            variant="contained"
            component="label"
            disabled={busy}
            startIcon={<FiUploadCloud />}
          >
            Rasm tanlash
            <input
              ref={inputRef}
              hidden
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => upload(e.target.files)}
            />
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatCard label="Jami tovar" value={overview?.total} icon={<FiImage />} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Rasmi bor" value={overview?.withPhotos} accent="#10B981" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Rasmi yo'q" value={overview?.withoutPhotos} accent="#F59E0B" />
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mb: 3 }}>
        Fayl nomi artikul bilan boshlanishi kerak — masalan{" "}
        <Box component="span" className="mono">14-1-9-0003.jpg</Box> yoki{" "}
        <Box component="span" className="mono">14-1-9-0003 (2).jpg</Box>. Bunday artikulli tovar
        1C da bo'lmasa, rasm saqlanmaydi va bekorga joy egallamaydi.
        <Box component="span" sx={{ display: "block", mt: 0.75 }}>
          Rasm yangilangan bo'lsa, <b>o'sha nom bilan qayta yuklayvering</b> — yangisi asosiy
          bo'ladi, eskisi tarixda qoladi. O'tkazib yuboriladigani faqat <b>aynan bir xil</b> fayl.
        </Box>
      </Alert>

      {busy && (
        <Paper sx={{ p: 2.5, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              Yuklanmoqda
            </Typography>
            <Num value={progress.done} suffix={`/ ${progress.total}`} />
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress.total ? (progress.done / progress.total) * 100 : 0}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Paper>
      )}

      {results.length > 0 && (
        <Paper sx={{ overflow: "hidden" }}>
          <Box sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4">Natija</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {linked} ta ulandi, {results.length - linked} ta o'tmadi
              </Typography>
            </Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filter}
              onChange={(_, v) => v && setFilter(v)}
            >
              <ToggleButton value="all">Hammasi</ToggleButton>
              {Object.keys(STATUS)
                .filter((k) => counts[k])
                .map((k) => (
                  <ToggleButton key={k} value={k}>
                    {STATUS[k].label} ({counts[k]})
                  </ToggleButton>
                ))}
            </ToggleButtonGroup>
          </Box>
          <Divider />
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fayl</TableCell>
                  <TableCell>Artikul</TableCell>
                  <TableCell>Tovar</TableCell>
                  <TableCell align="right">Holat</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((r, i) => (
                  <TableRow key={`${r.fileName}-${i}`} hover>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" noWrap title={r.fileName}>
                        {r.fileName}
                      </Typography>
                      {r.message && (
                        <Typography variant="caption" color="text.secondary">
                          {r.message}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box component="span" className="mono" sx={{ fontSize: "0.8125rem" }}>
                        {r.vendorCode || "—"}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap color="text.secondary">
                        {r.productName || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={STATUS[r.status]?.label || r.status}
                        color={STATUS[r.status]?.color || "default"}
                        variant={r.status === "LINKED" ? "filled" : "outlined"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {!busy && results.length === 0 && (
        <Paper
          sx={{
            p: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            borderStyle: "dashed",
          }}
        >
          <Box sx={{ fontSize: 28, color: "text.secondary", display: "flex" }}>
            <FiUploadCloud />
          </Box>
          <Typography variant="h4">Rasm tanlang</Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 380 }}>
            Bir vaqtda bir nechta fayl tanlash mumkin. Har biri bo'yicha natija shu yerda
            ko'rinadi.
          </Typography>
        </Paper>
      )}

      <PhotoLibrary onChanged={loadOverview} />
    </Box>
  );
};


/** "19.08.2026 12:26" */
const fmtWhen = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return null;
  return (
    d.toLocaleDateString("ru-RU") +
    " " +
    d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })
  );
};

/**
 * Kontakt-varaq katagi.
 *
 * Rasmlar bir-biriga tegib turadi va varaq bo'lib ko'rinadi -- kartochka
 * emas. Rasmi yo'q tovar bo'sh ramka bo'lib qoladi: 746 ta bo'shliq bir
 * qarashda ko'rinsin, chunki sahifaning asosiy savoli aynan shu.
 */
const Frame = ({ row, onOpen }) => {
  const first = row.photos?.[0];
  const src = first?.urls?.medium || first?.urls?.small || first?.urls?.thumbnail;
  return (
    <Box
      onClick={() => onOpen(row)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(row)}
      sx={{
        cursor: "pointer",
        bgcolor: "background.paper",
        position: "relative",
        outline: "1px solid transparent",
        outlineOffset: -1,
        transition: "outline-color .12s",
        "&:hover, &:focus-visible": { outlineColor: "primary.main" },
        "&:focus": { outline: "none" },
        "&:focus-visible": { outlineColor: "primary.main" },
      }}
    >
      <Box
        sx={{
          aspectRatio: "1 / 1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: src ? 1 : 0,
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={row.vendorCode}
            loading="lazy"
            sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
          />
        ) : (
          <Box
            sx={{
              width: "100%", height: "100%",
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "1px dashed", borderColor: "divider",
              color: "text.disabled", fontSize: 22,
            }}
          >
            <FiImage />
          </Box>
        )}
      </Box>
      {row.photoCount > 1 && (
        <Chip
          size="small"
          label={row.photoCount}
          sx={{ position: "absolute", top: 6, right: 6, height: 20, fontSize: 11 }}
        />
      )}
      <Box sx={{ px: 1, pb: 1 }}>
        <Typography className="mono" sx={{ fontSize: 11.5, lineHeight: 1.4 }} noWrap>
          {row.vendorCode}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>
          {row.name}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Rasmni ko'rish va almashtirish.
 *
 * Almashtirishda eski va yangi rasm yonma-yon turadi -- qaror shu yerda
 * qabul qilinadi, shuning uchun sahifadagi eng katta e'tibor shunga beriladi.
 */
const Viewer = ({ row, onClose, onPrev, onNext, onReplaced }) => {
  const [idx, setIdx] = useState(0);
  const [pending, setPending] = useState(null); // {file, url}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setIdx(0);
    setPending(null);
    setError(null);
  }, [row?.vendorCode]);

  useEffect(() => () => { if (pending?.url) URL.revokeObjectURL(pending.url); }, [pending]);

  if (!row) return null;
  const photos = row.photos || [];
  const current = photos[idx];
  const bigSrc = current?.urls?.original || current?.urls?.medium || current?.urls?.small;

  const pick = (file) => {
    if (!file) return;
    setError(null);
    setPending({ file, url: URL.createObjectURL(file) });
  };

  const confirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", pending.file);
      const { data } = await axios.post(
        `${API}/product/${encodeURIComponent(row.vendorCode)}/photo`,
        form,
        { headers: { Authorization: localStorage.getItem("token") } }
      );
      if (data?.status && data.status !== "LINKED") {
        setError(data.message || "Saqlanmadi");
        return;
      }
      setPending(null);
      await onReplaced();
    } catch (e) {
      setError(e?.response?.data?.message || "Saqlanmadi. Qayta urinib ko'ring.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, p: 2.5, pb: 2 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography className="mono" variant="h4">{row.vendorCode}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{row.name}</Typography>
          </Box>
          <IconButton size="small" onClick={onPrev} aria-label="Oldingi"><FiChevronLeft /></IconButton>
          <IconButton size="small" onClick={onNext} aria-label="Keyingi"><FiChevronRight /></IconButton>
          <IconButton size="small" onClick={onClose} aria-label="Yopish"><FiX /></IconButton>
        </Box>
        <Divider />

        {pending ? (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "1px", bgcolor: "divider" }}>
              {[
                { label: "Hozirgi", src: bigSrc, note: current?.fileName },
                { label: "Yangi", src: pending.url, note: pending.file.name },
              ].map((pane) => (
                <Box key={pane.label} sx={{ bgcolor: "background.paper", p: 2.5 }}>
                  <Typography variant="overline" color="text.secondary">{pane.label}</Typography>
                  <Box
                    sx={{
                      height: { xs: 220, md: 380 }, mt: 1,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: "action.hover",
                    }}
                  >
                    {pane.src ? (
                      <Box component="img" src={pane.src} alt={pane.label}
                        sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">Rasm yo'q</Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }} noWrap>
                    {pane.note || "—"}
                  </Typography>
                </Box>
              ))}
            </Box>
            {error && <Alert severity="error" sx={{ m: 2.5, mb: 0 }}>{error}</Alert>}
            <Box sx={{ p: 2.5, display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button onClick={() => setPending(null)} disabled={saving}>Bekor qilish</Button>
              <Button variant="contained" onClick={confirm} disabled={saving}>
                {saving ? "Almashtirilmoqda" : "Almashtirish"}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                height: { xs: 280, md: 460 },
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: "action.hover", p: 2,
              }}
            >
              {bigSrc ? (
                <Box component="img" src={bigSrc} alt={row.vendorCode}
                  sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
              ) : (
                <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                  <Box sx={{ fontSize: 30, display: "flex", justifyContent: "center", mb: 1 }}><FiImage /></Box>
                  <Typography variant="body2">Bu tovarda rasm yo'q</Typography>
                </Box>
              )}
            </Box>

            {photos.length > 1 && (
              <Box sx={{ display: "flex", gap: 1, p: 2, pb: 0, flexWrap: "wrap" }}>
                {photos.map((ph, i) => (
                  <Box
                    key={ph.id}
                    onClick={() => setIdx(i)}
                    sx={{
                      width: 56, height: 56, cursor: "pointer", p: 0.5,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      outline: "1px solid",
                      outlineColor: i === idx ? "primary.main" : "divider",
                      outlineOffset: -1,
                    }}
                  >
                    <Box component="img" src={ph.urls?.thumbnail || ph.urls?.small} alt=""
                      sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                  </Box>
                ))}
              </Box>
            )}

            {error && <Alert severity="error" sx={{ m: 2.5, mb: 0 }}>{error}</Alert>}

            <Box sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>{current?.fileName || "Fayl yo'q"}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {fmtWhen(current?.uploadedAt) || "Yuklanmagan"}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<FiRefreshCw />}
                onClick={() => fileRef.current?.click()}
              >
                {photos.length ? "Rasmni almashtirish" : "Rasm yuklash"}
              </Button>
            </Box>
          </>
        )}

        <input ref={fileRef} hidden type="file" accept="image/*"
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      </DialogContent>
    </Dialog>
  );
};

/**
 * Yuklangan rasmlar.
 *
 * Ilgari bu sahifada faqat yuklash tugmasi turardi: yuklab bo'lgach qaysi
 * artikulda rasm bor, qaysi fayl va qachon yuklangani ko'rinmasdi.
 */
const PhotoLibrary = ({ onChanged }) => {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [mode, setMode] = useState("all");
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(48);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ mode, page: String(page), size: String(size) });
      if (search.trim()) params.set("search", search.trim());
      const { data } = await axios.get(`${API}/product/photos/list?${params}`, {
        headers: { Authorization: localStorage.getItem("token") },
      });
      setRows(data?.data || []);
      setTotalRows(data?.totalElements ?? 0);
    } catch {
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [mode, page, size, search]);

  // Qidiruv har harfda so'rov yubormasin
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const afterReplace = async () => {
    await load();
    onChanged?.();
  };

  const step = (d) => {
    setOpenIdx((i) => {
      if (i === null) return i;
      const n = i + d;
      return n < 0 || n >= rows.length ? i : n;
    });
  };

  const emptyText =
    mode === "without" ? "Hamma tovarda rasm bor."
      : mode === "with" ? "Hali birorta rasm yuklanmagan."
      : "Tovar topilmadi.";

  return (
    <Paper sx={{ overflow: "hidden", mt: 3 }}>
      <Box sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1, minWidth: 180 }}>
          <Typography variant="h4">Yuklangan rasmlar</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Rasmni bosing — kattalashadi va shu yerdan almashtiriladi
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Artikul yoki nomi..."
          value={search}
          onChange={(e) => { setPage(0); setSearch(e.target.value); }}
          InputProps={{ startAdornment: <Box sx={{ display: "flex", mr: 1, color: "text.secondary" }}><FiSearch /></Box> }}
          sx={{ width: 240 }}
        />
        <ToggleButtonGroup size="small" exclusive value={mode}
          onChange={(_, v) => { if (v) { setPage(0); setMode(v); } }}>
          <ToggleButton value="all">Hammasi</ToggleButton>
          <ToggleButton value="with">Rasmi bor</ToggleButton>
          <ToggleButton value="without">Rasmi yo'q</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="grid" aria-label="Katakcha"><FiGrid /></ToggleButton>
          <ToggleButton value="list" aria-label="Ro'yxat"><FiList /></ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Divider />

      {loading && <LinearProgress />}

      {!loading && rows.length === 0 && (
        <Box sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="body2">{emptyText}</Typography>
        </Box>
      )}

      {view === "grid" && rows.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(148px, 1fr))",
            gap: "1px",
            bgcolor: "divider",
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {rows.map((r, i) => <Frame key={r.id} row={r} onOpen={() => setOpenIdx(i)} />)}
        </Box>
      )}

      {view === "list" && rows.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 64 }}>Rasm</TableCell>
                <TableCell>Artikul</TableCell>
                <TableCell>Nomi</TableCell>
                <TableCell>Fayl</TableCell>
                <TableCell>Yuklangan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => {
                const first = r.photos?.[0];
                const thumb = first?.urls?.thumbnail || first?.urls?.small;
                return (
                  <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => setOpenIdx(i)}>
                    <TableCell>
                      <Box sx={{
                        width: 44, height: 44, display: "flex",
                        alignItems: "center", justifyContent: "center",
                        bgcolor: thumb ? "transparent" : "action.hover",
                        color: "text.disabled",
                      }}>
                        {thumb
                          ? <Box component="img" src={thumb} alt="" loading="lazy"
                              sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                          : <FiImage />}
                      </Box>
                    </TableCell>
                    <TableCell><Box component="span" className="mono">{r.vendorCode}</Box></TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>{r.name}</TableCell>
                    <TableCell sx={{ color: "text.secondary", maxWidth: 260 }}>
                      <Box component="span" sx={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>
                        {first?.fileName || "—"}
                      </Box>
                      {r.photoCount > 1 && <Chip size="small" label={`+${r.photoCount - 1}`} sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                      {fmtWhen(r.lastUploadedAt) || "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        component="div"
        count={totalRows}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={size}
        onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[24, 48, 96]}
        labelRowsPerPage="Qatorlar soni:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} dan ${count}`}
      />

      {openIdx !== null && rows[openIdx] && (
        <Viewer
          row={rows[openIdx]}
          onClose={() => setOpenIdx(null)}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          onReplaced={afterReplace}
        />
      )}
    </Paper>
  );
};

export default ProductsPage;
